import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { Building2, Users, TrendingUp, Activity, ArrowRight, Plus } from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { format } from 'date-fns';

const COLORS = ['#3b82f6', '#8b5cf6', '#10b981'];

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="card flex items-center gap-4">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
      <Icon className="w-6 h-6 text-white" />
    </div>
    <div>
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </div>
  </div>
);

const SuperAdminDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/super-admin/dashboard').then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" /></div>;

  const { stats, recentTenants, planDistribution } = data || {};

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Dashboard</h1>
          <p className="text-gray-500 text-sm mt-1">Multi-tenant DMS overview</p>
        </div>
        <Link to="/super-admin/tenants/new" className="btn-primary">
          <Plus className="w-4 h-4" /> New Tenant
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard icon={Building2} label="Total Tenants" value={stats?.totalTenants || 0} color="bg-primary-600" />
        <StatCard icon={Activity} label="Active Tenants" value={stats?.activeTenants || 0} color="bg-green-500" />
        <StatCard icon={TrendingUp} label="Trial Tenants" value={stats?.trialTenants || 0} color="bg-yellow-500" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Plan Distribution */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Plan Distribution</h2>
          {planDistribution?.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={planDistribution} dataKey="count" nameKey="plan" cx="50%" cy="50%" outerRadius={80} label={({ plan, count }) => `${plan}: ${count}`}>
                  {planDistribution.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-gray-400 text-center py-10">No data yet</p>}
        </div>

        {/* Recent Tenants */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800">Recent Tenants</h2>
            <Link to="/super-admin/tenants" className="text-primary-600 text-sm hover:underline flex items-center gap-1">
              View all <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="space-y-3">
            {recentTenants?.length === 0 && <p className="text-gray-400 text-sm text-center py-8">No tenants yet. Create one to get started.</p>}
            {recentTenants?.map(t => (
              <div key={t.id} className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 hover:bg-gray-50">
                <div className="w-9 h-9 bg-primary-100 rounded-lg flex items-center justify-center font-bold text-primary-700 text-sm">
                  {t.name[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-gray-900 truncate">{t.name}</p>
                  <p className="text-xs text-gray-400">{t.slug} · {format(new Date(t.created_at), 'MMM d, yyyy')}</p>
                </div>
                <span className={`badge-${t.status}`}>{t.status}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
